import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listPublishedCourses, getMyPointsHistory, listMyBadges } from "@/lib/academy.functions";
import { CourseCard, CourseCardRow } from "@/components/academy/CourseCard";
import { Trophy, BookOpen, Award, LifeBuoy } from "lucide-react";

export const Route = createFileRoute("/aluno/")({
  ssr: false,
  component: AlunoDashboard,
});

function AlunoDashboard() {
  const courses = useQuery({ queryKey: ["academy", "courses"], queryFn: () => listPublishedCourses(), staleTime: 60_000 });
  const points = useQuery({ queryKey: ["academy", "points"], queryFn: () => getMyPointsHistory(), staleTime: 60_000 });
  const badges = useQuery({ queryKey: ["academy", "badges"], queryFn: () => listMyBadges(), staleTime: 60_000 });

  return (
    <div className="space-y-10">
      <div>
        <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-primary">
          Área de membros
        </span>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight md:text-4xl">
          Bem-vindo à <span className="text-primary">Academia SVG</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Continue de onde parou ou comece um novo módulo. Conteúdo exclusivo para membros.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard icon={Trophy} label="Pontos totais" value={points.data?.total ?? 0} accent />
        <StatCard icon={BookOpen} label="Cursos disponíveis" value={courses.data?.length ?? 0} />
        <StatCard icon={Award} label="Badges" value={badges.data?.length ?? 0} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/[0.06] p-4">
        <div className="flex items-center gap-3">
          <LifeBuoy className="h-5 w-5 text-primary" />
          <div>
            <div className="text-sm font-semibold">Precisa de ajuda?</div>
            <div className="text-xs text-muted-foreground">
              Fale com o suporte para resetar sua senha ou tirar dúvidas sobre as aulas.
            </div>
          </div>
        </div>
        <a
          href="https://wa.me/555596852045?text=Ol%C3%A1%21%20Sou%20aluno%20da%20Academia%20SVG%20e%20preciso%20de%20suporte."
          target="_blank"
          rel="noopener"
          className="btn-primary text-xs"
        >
          Falar com o suporte no WhatsApp
        </a>
      </div>

      {courses.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando módulos…</p>
      ) : !courses.data || courses.data.length === 0 ? (
        <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045] p-8 text-center text-sm text-muted-foreground">
          Nenhum módulo publicado ainda. Volte em breve — estamos preparando conteúdos incríveis.
        </div>
      ) : (
        <>
          <CourseCardRow title="Meus cursos em progresso">
            {courses.data.slice(0, 6).map((c: any, i: number) => (
              <div key={c.id} className="w-56 shrink-0 snap-start sm:w-60">
                <CourseCard
                  slug={c.slug}
                  title={c.title}
                  cover_url={c.cover_url}
                  category={c.category}
                  level={c.level}
                  index={i + 1}
                />
              </div>
            ))}
          </CourseCardRow>

          <CourseCardRow title="Conteúdo fechado · exclusivo">
            {courses.data.map((c: any, i: number) => (
              <div key={`k-${c.id}`} className="w-56 shrink-0 snap-start sm:w-60">
                <CourseCard
                  slug={c.slug}
                  title={c.title}
                  cover_url={c.cover_url}
                  category={c.category}
                  level={c.level}
                  index={i + 1}
                />
              </div>
            ))}
          </CourseCardRow>
        </>
      )}

      {points.data?.history?.length ? (
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Histórico recente de pontos
          </h2>
          <ul className="divide-y divide-ink/12 rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045]">
            {points.data.history.slice(0, 8).map((p: any) => (
              <li key={p.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="text-muted-foreground">{p.reason}</span>
                <span className="font-semibold text-primary">+{p.points}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border p-4 ${
        accent
          ? "border-primary/40 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent"
          : "border-ink/10 bg-ink/[0.045]"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className={`mt-2 text-3xl font-extrabold ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}